<template>
  <div>
    <h2 class="content-block">Gateway Overview</h2>
    <dx-data-grid
      ref="overview-grid"
      :data-source="dataSource"
      :key-expr="'gateway'"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      class="dx-card wide-card"
      width="100%"
    >
      <dx-column-chooser :enabled="false" />
      <dx-export
        :enabled="true"
        :file-name="`gateway-overview-${currentDate}`"
      />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column
        data-field="gateway"
        :lookup="gatewayColumnLookup"
        caption="Gateway"
      />
      <dx-column
        data-field="source"
        :lookup="comparisonSourceTypeColumnLookup"
        caption="Source"
      />
      <dx-column
        data-field="currencyId"
        :lookup="currencyColumnLookup"
        caption="Currency"
      />
      <dx-column
        data-field="unmatchedCount"
        :format="config.countFormat"
        caption="Unmatched"
      />
      <dx-column
        data-field="unmatchedAmount"
        :format="config.financialNumberFormat"
      />
      <dx-column
        data-field="reviewCount"
        :format="config.countFormat"
        caption="Reviews"
      />
      <dx-column
        data-field="mostRecentRecord"
        caption="Most Recent Transaction"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="balanceTimestamp"
        caption="Balance reported"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="summedBalance"
        :format="config.financialNumberFormat"
      />
      <dx-column
        data-field="reportedBalance"
        :format="config.financialNumberFormat"
      />
      <dx-column
        data-field="balanceDifference"
        caption="Balance difference"
        :format="config.financialNumberFormat"
      />
      <dx-column
        data-field="actionData"
        caption="Actions"
        cell-template="button-cell-template"
        :allow-sorting="false"
      />
      <template #button-cell-template="cell">
        <div>
          <dx-button
            :visible="cell.data.value.unmatchedCount > 0"
            icon="rowfield"
            text="Match"
            @click="openMatching(cell.data.value.gateway)"
          />
          <dx-button
            :visible="cell.data.value.reviewCount > 0"
            icon="search"
            text="Review"
            @click="openReview(cell.data.value.gateway)"
          />
          <dx-button
            :visible="cell.data.value.balanceAvailable > 0"
            icon="chart"
            text="Balance"
            @click="openBalance(cell.data.value.gateway, cell.data.value.source)"
          />
          <dx-button
            icon="datafield"
            text="Ledger"
            @click="openLedger(cell.data.value.gateway, cell.data.value.source)"
          />
          <dx-button
            :visible="cell.data.value.source === 'LEDGER'"
            icon="copy"
            text="Auto Fees"
            @click="
              openFee(
                cell.data.value.gateway,
                cell.data.value.source,
                cell.data.value.currencyId,
              )
            "
          />
        </div>
      </template>
    </dx-data-grid>
  </div>
</template>

<script>
	import {mapState} from 'vuex'
	import {
		DxColumn,
		DxColumnChooser,
		DxDataGrid,
		DxExport,
		DxFilterPanel,
		DxFilterRow,
		DxHeaderFilter,
		DxPager,
		DxPaging,
	} from 'devextreme-vue/data-grid'
	import {DxButton} from 'devextreme-vue/button'
	import {DxTagBox} from 'devextreme-vue'
	import {http} from '@/utils/http'
	import {config} from '@/config'

	export default {
  name: 'ViewStatement',
  components: {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
  },
  data() {
    return {
      config,
      dataSource: null,
      pageSizes: config.itemsPerPage,
    }
  },
  computed: {
    ...mapState('user', [
      'currencyColumnLookup',
      'gatewayColumnLookup',
      'comparisonSourceTypeColumnLookup',
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.loadData()
  },
  destroyed() {},
  methods: {
    async loadData() {
      const response = await http
        .get('reconciliation/admin/gateway/overview')
        .json()
      const rowData = []
      response.gateways.forEach((gateway) => {
        gateway.sources.forEach((source) => {
          source.gateway = gateway.key
		  source.currencyId = gateway.currencyId
          source['actionData'] = {
            gateway: source.gateway,
            currencyId: source.currencyId,
            balanceAvailable: source.reportedBalance !== null,
            unmatchedCount: source.unmatchedCount,
            reviewCount: source.reviewCount,
            source: source.source,
          }
          rowData.push(source)
        })
      })
      this.dataSource = rowData
    },
    openBalance(gateway, source) {
      this.$router.push({ path: `/balance/${gateway}/${source}` })
    },
    openMatching(gateway) {
      this.$router.push({ path: `/matching/${gateway}` })
    },
    openReview(gateway) {
      this.$router.push({ path: `/review/${gateway}` })
    },
    openFee(gateway, source, currencyId) {
      this.$router.push({
        path: `/fee/auto/${gateway}/${source}/${currencyId}`,
      })
    },
    openLedger(gateway, source) {
      this.$router.push({ path: `/accounts-report/${gateway}/${source}` })
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}

.dx-button {
  margin-right: 5px;
  margin-bottom: 5px;
}
</style>
